'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState, type ReactNode } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { MediaPlaceholder } from '../ui/MediaPlaceholder';
import { cn } from '@/lib/utils';
import {
  Bell,
  ShieldAlert,
  ShieldCheck,
  UserPlus,
  Timer,
  Mail,
  MapPin,
  Handshake,
  Waves,
  Mic,
  MicOff,
  EyeOff,
  type LucideIcon,
} from 'lucide-react';
import { useTranslations } from 'next-intl';

const richLinkClass = 'text-accent underline underline-offset-2 hover:text-accent/80';

type StepConfig = {
  key: string;
  icon: LucideIcon;
  images: string[];
  videoPoster?: string;
  videoSrc?: string;
  richEaritLink?: boolean;
  richSettingsLink?: boolean;
  richSafetizenControlsLink?: boolean;
  hasPoints?: boolean;
};

type ChapterConfig = {
  key: string;
  steps: StepConfig[];
};

type SectionConfig = {
  sectionKey: 'safetizen' | 'earit';
  logo: string;
  optional?: boolean;
  chapters: ChapterConfig[];
};

const MAX_SCREENSHOTS = 2;

function limitImages(images: string[]) {
  return images.slice(0, MAX_SCREENSHOTS);
}

const SECTIONS: SectionConfig[] = [
  {
    sectionKey: 'safetizen',
    logo: '/images/safetizen-logo.svg',
    chapters: [
      {
        key: 'gettingStarted',
        steps: [
          {
            key: 'permissions',
            icon: ShieldCheck,
            images: [],
            hasPoints: true,
          },
          {
            key: 'referents',
            icon: UserPlus,
            images: [
              '/images/safetizen_screen_no_referent.png',
              '/images/safetizen_screen_add_new_referent.png',
            ],
            hasPoints: true,
          },
          {
            key: 'privateMode',
            icon: EyeOff,
            images: [
              '/images/safetizen_screen_privacy_mode_settings.png',
              '/images/safetizen_screen_privacy_mode.png',
            ],
            hasPoints: true,
          },
          {
            key: 'earitControls',
            icon: MicOff,
            images: ['/images/safetizen_screen_earit_settings.png'],
            richSettingsLink: true,
            hasPoints: true,
          },
        ],
      },
      {
        key: 'triggerAlert',
        steps: [
          {
            key: 'trigger',
            icon: ShieldAlert,
            images: ['/images/safetizen_screen_widget.png', '/images/safetizen_screen_idle.png'],
            richEaritLink: true,
          },
          {
            key: 'forYou',
            icon: Timer,
            images: [
              '/images/safetizen_screen_alert_is_sending.png',
              '/images/safetizen_screen_alert_sent.png',
            ],
            hasPoints: true,
          },
        ],
      },
      {
        key: 'whoReceives',
        steps: [
          {
            key: 'forReferents',
            icon: Mail,
            images: [],
            hasPoints: true,
          },
          {
            key: 'forUsers',
            icon: MapPin,
            images: [
              '/images/safetizen_screen_help_alert.png',
              '/images/safetizen_screen_alert_notif.png',
            ],
            hasPoints: true,
          },
        ],
      },
      {
        key: 'helpSomeone',
        steps: [
          {
            key: 'joinAlert',
            icon: Handshake,
            images: [
              '/images/safetizen_screen_is_helping.png',
              '/images/safetizen_screen_alert_ended.png',
            ],
            hasPoints: true,
          },
        ],
      },
    ],
  },
  {
    sectionKey: 'earit',
    logo: '/images/earit-logo.svg',
    optional: true,
    chapters: [
      {
        key: 'module',
        steps: [
          {
            key: 'configure',
            icon: Waves,
            images: ['/images/earit_screen_settings.png'],
            richSafetizenControlsLink: true,
            hasPoints: true,
          },
          {
            key: 'recordings',
            icon: Mic,
            images: ['/images/safetizen_screen_records.png'],
            hasPoints: true,
          },
          {
            key: 'autoTrigger',
            icon: Bell,
            images: ['/images/safetizen_screen_verbal_assault_notif.png'],
            hasPoints: true,
          },
        ],
      },
    ],
  },
];

function chapterAnchor(sectionKey: string, chapterKey: string) {
  return `chapter-${sectionKey}-${chapterKey}`;
}

function stepAnchor(sectionKey: string, stepKey: string) {
  return `${sectionKey}-${stepKey}`;
}

function StepDescription({
  sectionKey,
  stepKey,
  richEaritLink,
  richSettingsLink,
  richSafetizenControlsLink,
  hasPoints,
}: {
  sectionKey: string;
  stepKey: string;
  richEaritLink?: boolean;
  richSettingsLink?: boolean;
  richSafetizenControlsLink?: boolean;
  hasPoints: boolean;
}) {
  const t = useTranslations('pages.howItWorks');
  const base = `${sectionKey}.steps.${stepKey}`;
  const points = hasPoints ? (t.raw(`${base}.points`) as string[]) : [];

  const richComponents: Record<string, (chunks: ReactNode) => ReactNode> = {};
  if (richEaritLink) {
    richComponents.earitLink = (chunks: ReactNode) => (
      <Link href="/earit" className={richLinkClass}>
        {chunks}
      </Link>
    );
  }
  if (richSettingsLink) {
    richComponents.settingsLink = (chunks: ReactNode) => (
      <Link href="#earit-configure" className={richLinkClass}>
        {chunks}
      </Link>
    );
  }
  if (richSafetizenControlsLink) {
    richComponents.safetizenLink = (chunks: ReactNode) => (
      <Link href="#safetizen-earitControls" className={richLinkClass}>
        {chunks}
      </Link>
    );
  }

  const description =
    Object.keys(richComponents).length > 0
      ? t.rich(`${base}.description`, richComponents)
      : t(`${base}.description`);

  return (
    <div className="space-y-3">
      <p className="text-foreground/85 text-base leading-relaxed">{description}</p>
      {hasPoints ? (
        <ul className="space-y-2 text-sm text-foreground/80">
          {points.map((point) => (
            <li key={point} className="flex gap-2">
              <span className="text-accent shrink-0 mt-0.5">•</span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function StepMedia({
  images,
  title,
  videoSrc,
  videoComingSoon,
  screenshotComingSoon,
}: {
  images: string[];
  title: string;
  videoSrc?: string;
  videoComingSoon: string;
  screenshotComingSoon: string;
}) {
  const cappedImages = limitImages(images);

  if (videoSrc) {
    return (
      <MediaPlaceholder
        poster={cappedImages[0] ?? '/images/normal_light.png'}
        label={title}
        comingSoon={videoComingSoon}
        videoSrc={videoSrc}
      />
    );
  }

  if (cappedImages.length === 0) {
    return (
      <div className="flex aspect-9/19 max-h-[480px] w-full items-center justify-center rounded-xl border border-dashed border-accent/30 bg-muted/50 px-6 text-center">
        <p className="text-sm text-foreground/55">{screenshotComingSoon}</p>
      </div>
    );
  }

  if (cappedImages.length === 2) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {cappedImages.map((src) => (
          <div
            key={src}
            className="relative aspect-9/19 max-h-[420px] w-full overflow-hidden rounded-xl border border-accent/20 bg-white shadow-sm"
          >
            <Image
              src={src}
              alt={title}
              fill
              className="object-contain object-top"
              sizes="(max-width: 768px) 45vw, 25vw"
            />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="relative mx-auto aspect-9/19 max-h-[480px] w-full overflow-hidden rounded-xl border border-accent/20 bg-white shadow-sm">
      <Image
        src={cappedImages[0]}
        alt={title}
        fill
        className="object-contain object-top"
        sizes="(max-width: 768px) 100vw, 50vw"
      />
    </div>
  );
}

function SectionBlock({ section }: { section: SectionConfig }) {
  const t = useTranslations('pages.howItWorks');
  const showChapterHeadings = !section.optional;

  return (
    <section
      id={`section-${section.sectionKey}`}
      className={cn(
        'scroll-mt-28',
        section.optional && 'rounded-2xl border border-purple/20 bg-white/60 p-6 sm:p-8 lg:p-10'
      )}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8">
        <Image
          src={section.logo}
          alt={t(`${section.sectionKey}.title`)}
          width={48}
          height={48}
          className="h-11 w-auto shrink-0"
        />
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground">
              {section.optional ? t('earit.appendixTitle') : t(`${section.sectionKey}.title`)}
            </h2>
            {section.optional ? (
              <Badge variant="default" className="text-xs px-2 py-0.5">
                {t(`${section.sectionKey}.optionalBadge`)}
              </Badge>
            ) : null}
          </div>
          <p className="text-foreground/75 mt-1">
            {section.optional ? t('earit.appendixSubtitle') : t(`${section.sectionKey}.subtitle`)}
          </p>
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10 xl:grid-cols-[280px_minmax(0,1fr)]">
        <nav
          className="mb-8 lg:mb-0 lg:sticky lg:top-32 lg:self-start"
          aria-label={t('tocTitle')}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-foreground/50 mb-3 hidden lg:block">
            {t('tocTitle')}
          </p>
          <div className="flex lg:flex-col gap-4 overflow-x-auto pb-2 lg:pb-0 lg:overflow-visible">
            {section.chapters.map((chapter, chapterIndex) => (
              <div key={chapter.key} className="shrink-0 lg:shrink">
                {showChapterHeadings ? (
                  <a
                    href={`#${chapterAnchor(section.sectionKey, chapter.key)}`}
                    className="block text-xs font-semibold uppercase tracking-wide text-accent mb-2 hover:text-accent/80"
                  >
                    {t(`${section.sectionKey}.chapters.${chapter.key}.title`)}
                  </a>
                ) : null}
                <ul className={cn('flex lg:flex-col gap-2', showChapterHeadings && 'lg:pl-0')}>
                  {chapter.steps.map((step, stepIndex) => {
                    const title = t(`${section.sectionKey}.steps.${step.key}.title`);
                    const stepNumber = showChapterHeadings
                      ? `${chapterIndex + 1}.${stepIndex + 1}`
                      : `${stepIndex + 1}`;

                    return (
                      <li key={step.key} className="shrink-0 lg:shrink">
                        <a
                          href={`#${stepAnchor(section.sectionKey, step.key)}`}
                          className="block rounded-lg border border-accent/20 bg-white px-3 py-2 text-sm text-foreground/85 hover:border-accent/50 hover:text-accent transition-colors lg:border-transparent lg:bg-transparent lg:px-0 lg:py-1.5"
                        >
                          <span className="text-accent/70 font-medium mr-1.5">{stepNumber}</span>
                          {title}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        <div className="flex flex-col gap-14">
          {section.chapters.map((chapter, chapterIndex) => (
            <div
              key={chapter.key}
              id={showChapterHeadings ? chapterAnchor(section.sectionKey, chapter.key) : undefined}
              className="scroll-mt-32 flex flex-col gap-8"
            >
              {showChapterHeadings ? (
                <header className="border-b border-accent/15 pb-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-accent mb-1">
                    {t('chapterLabel', { number: chapterIndex + 1 })}
                  </p>
                  <h3 className="text-xl md:text-2xl font-bold text-foreground">
                    {t(`${section.sectionKey}.chapters.${chapter.key}.title`)}
                  </h3>
                  <p className="text-foreground/75 mt-1 text-sm md:text-base">
                    {t(`${section.sectionKey}.chapters.${chapter.key}.subtitle`)}
                  </p>
                </header>
              ) : null}

              {chapter.steps.map((step, stepIndex) => {
                const Icon = step.icon;
                const title = t(`${section.sectionKey}.steps.${step.key}.title`);
                const stepNumber = showChapterHeadings
                  ? `${chapterIndex + 1}.${stepIndex + 1}`
                  : `${stepIndex + 1}`;

                return (
                  <article
                    key={step.key}
                    id={stepAnchor(section.sectionKey, step.key)}
                    className="scroll-mt-32"
                  >
                    <Card className="border-accent/20 shadow-sm bg-white p-5 sm:p-6 lg:p-8">
                      <header className="flex items-start gap-3 mb-5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent text-sm font-bold">
                          {stepNumber}
                        </span>
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
                          <Icon className="w-4 h-4" aria-hidden />
                        </div>
                        <h4 className="text-xl font-semibold text-foreground pt-1">{title}</h4>
                      </header>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
                        <div>
                          <StepMedia
                            images={step.images}
                            title={title}
                            videoSrc={step.videoSrc}
                            videoComingSoon={t('videoComingSoon')}
                            screenshotComingSoon={t('screenshotComingSoon')}
                          />
                        </div>
                        <StepDescription
                          sectionKey={section.sectionKey}
                          stepKey={step.key}
                          richEaritLink={step.richEaritLink}
                          richSettingsLink={step.richSettingsLink}
                          richSafetizenControlsLink={step.richSafetizenControlsLink}
                          hasPoints={step.hasPoints ?? false}
                        />
                      </div>
                    </Card>
                  </article>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const t = useTranslations('pages.howItWorks');
  const [activeSection, setActiveSection] = useState<'safetizen' | 'earit'>('safetizen');

  useEffect(() => {
    const elements = SECTIONS.map((section) => document.getElementById(`section-${section.sectionKey}`)).filter(
      Boolean
    ) as HTMLElement[];

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        const topEntry = visible[0];
        if (!topEntry) return;

        const sectionId = topEntry.target.id.replace('section-', '');
        if (sectionId === 'safetizen' || sectionId === 'earit') {
          setActiveSection(sectionId);
        }
      },
      { rootMargin: '-35% 0px -45% 0px', threshold: [0, 0.15, 0.35, 0.55, 0.75] }
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="py-16 md:py-20 px-4 bg-pink-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">{t('title')}</h1>
          <p className="text-lg text-foreground/85 leading-relaxed">{t('subtitle')}</p>
        </div>

        <nav
          className="sticky top-16 z-30 -mx-4 px-4 py-3 mb-10 bg-pink-white/95 backdrop-blur border-y border-accent/15"
          aria-label={t('navLabel')}
        >
          <p className="text-center text-xs font-semibold uppercase tracking-wide text-foreground/50 mb-2">
            {t('navLabel')}
          </p>
          <div className="max-w-7xl mx-auto flex justify-center gap-3">
            {SECTIONS.map((section) => {
              const isActive = activeSection === section.sectionKey;

              return (
                <a
                  key={section.sectionKey}
                  href={`#section-${section.sectionKey}`}
                  onClick={() => setActiveSection(section.sectionKey)}
                  aria-current={isActive ? 'true' : undefined}
                  className={cn(
                    'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all shadow-sm border text-navy',
                    isActive
                      ? 'bg-white border-navy ring-2 ring-navy/25 shadow-md'
                      : 'bg-white border-navy/15 hover:border-navy/35'
                  )}
                >
                  <Image
                    src={section.logo}
                    alt=""
                    width={20}
                    height={20}
                    className="h-5 w-auto"
                    aria-hidden
                  />
                  {t(`nav.${section.sectionKey}`)}
                </a>
              );
            })}
          </div>
        </nav>

        <div className="max-w-4xl mx-auto mb-12">
          <p className="text-center text-sm font-medium text-foreground/70 mb-3">
            {t('overviewVideo.title')}
          </p>
          <MediaPlaceholder
            poster="/images/normal_light.png"
            label={t('overviewVideo.title')}
            comingSoon={t('videoComingSoon')}
          />
        </div>

        <div className="flex flex-col gap-16 lg:gap-20">
          {SECTIONS.map((section) => (
            <SectionBlock key={section.sectionKey} section={section} />
          ))}
        </div>
      </div>
    </div>
  );
}
